import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { bdDashboardAPI } from "../../api";
import TurbineAnimation from "../../components/turbine-animation/TurbineAnimation";
import WplWindfarmsMap from "./WplWindfarmsMap";
import WplWindfarmDetail from "./WplWindfarmDetail";
import { anonymizeRows, chartFont, formatDateTime } from "./wplDashboardHelpers";

const WINDOW_OPTIONS = [
  { value: 14, label: "Last 2 weeks" },
  { value: 30, label: "Last month" },
  { value: 90, label: "Last 3 months" },
];

// Screensaver: the Lassie overview dwell (30s at page level) shows the
// activity chart for the first half, then flips to the table view.
const SCREENSAVER_VIEW_SWITCH_MS = 15000;

const FARM_COLUMNS = [
  { key: "windfarm_name", label: "Windfarm" },
  { key: "customer", label: "Customer" },
  { key: "turbine_count", label: "Turbines" },
  { key: "strike_count", label: "Strikes" },
  { key: "outside_iec_count", label: "Outside IEC" },
  { key: "last_notification_at", label: "Last notification" },
];

const chartTheme = {
  text: "#ffffff",
  muted: "#d3e3dd",
  grid: "#3f7468",
  tooltipBg: "#1b443e",
  border: "#3f7468",
  accentAlert: "#f5894a",
  accentBrand: "#38b97c",
};

const formatLongLabel = (label) => {
  if (!label) return "";
  const value = String(label);
  if (value.length <= 20) return value;
  return `${value.slice(0, 17)}...`;
};

// Y-axis tick that acts as a link: clicking a windfarm name selects that
// farm; hovering it highlights the farm on the map.
function FarmAxisTick({ x, y, payload, onSelect, onHover }) {
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      className="bd-chart-tick-link"
      fill={chartTheme.muted}
      fontSize={chartFont(13)}
      onClick={() => onSelect(payload.value)}
      onMouseEnter={() => onHover(payload.value)}
      onMouseLeave={() => onHover(null)}
    >
      <title>{payload.value}</title>
      {formatLongLabel(payload.value)}
    </text>
  );
}

export default function WplActiveWindfarmsSection({
  screensaverPhase,
  onScreensaverReady,
}) {
  const [days, setDays] = useState(14);
  const [windfarms, setWindfarms] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [activityView, setActivityView] = useState("chart");
  const [farmSort, setFarmSort] = useState({ key: "strike_count", dir: "desc" });
  const [hoveredFarmId, setHoveredFarmId] = useState(null);
  const onScreensaverReadyRef = useRef(onScreensaverReady);
  onScreensaverReadyRef.current = onScreensaverReady;
  const [farmDetail, setFarmDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!selectedFarm) return undefined;
    let isMounted = true;
    setDetailLoading(true);
    setDetailError(null);
    setFarmDetail(null);
    bdDashboardAPI
      .activeWindfarmDetail({ windfarm_id: selectedFarm.windfarm_id })
      .then((data) => {
        if (isMounted) setFarmDetail(data);
      })
      .catch((err) => {
        if (isMounted) setDetailError(err);
      })
      .finally(() => {
        if (isMounted) setDetailLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedFarm]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    bdDashboardAPI
      .activeWindfarmsMap({ days })
      .then((data) => {
        if (!isMounted) return;
        setWindfarms(
          Array.isArray(data?.windfarms) ? anonymizeRows(data.windfarms) : []
        );
        setCounts(data?.counts ?? null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setWindfarms([]);
        setCounts(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [days]);

  const chartData = useMemo(
    () =>
      windfarms.map((farm) => ({
        windfarm_id: farm.windfarm_id,
        name: farm.windfarm_name,
        within_iec: Math.max(farm.strike_count - farm.outside_iec_count, 0),
        outside_iec: farm.outside_iec_count,
        total: farm.strike_count,
        farm,
      })),
    [windfarms]
  );

  // Distinct clients among the subscribed windfarms (already anonymized, so
  // the label is the identity).
  const clientCount = useMemo(
    () =>
      new Set(
        windfarms.map((farm) => farm.customer || farm.owner).filter(Boolean)
      ).size,
    [windfarms]
  );

  const chartHeight =Math.max(240, chartData.length * 34);

  const toggleFarmSort = (key) =>
    setFarmSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" }
    );

  const sortedFarms = useMemo(() => {
    const factor = farmSort.dir === "desc" ? -1 : 1;
    return [...windfarms].sort((a, b) => {
      const left = a[farmSort.key];
      const right = b[farmSort.key];
      if (left === right) return 0;
      if (left === null || left === undefined) return 1;
      if (right === null || right === undefined) return -1;
      return (left < right ? -1 : 1) * factor;
    });
  }, [windfarms, farmSort]);

  const focusFarm = (farm) => {
    if (!farm) return;
    setFocusRequest({ farm, key: Date.now() });
    setSelectedFarm(farm);
  };

  const hoverFarmByName = (name) => {
    if (!name) {
      setHoveredFarmId(null);
      return;
    }
    const entry = chartData.find((item) => item.name === name);
    setHoveredFarmId(entry?.farm?.windfarm_id ?? null);
  };

  const closeDetail = () => {
    setSelectedFarm(null);
    setFarmDetail(null);
    // Return the map to the all-farms overview.
    setFocusRequest({ farm: null, key: Date.now() });
  };

  // Screensaver: the overview phase is ready once the farm list is loaded.
  useEffect(() => {
    if (screensaverPhase === "overview" && !loading) {
      onScreensaverReadyRef.current?.("overview");
    }
  }, [screensaverPhase, loading]);

  // Screensaver: during the overview dwell, show the chart first and switch
  // to the table halfway through.
  useEffect(() => {
    if (screensaverPhase !== "overview" || loading) return undefined;
    setActivityView("chart");
    const switchTimerId = setTimeout(
      () => setActivityView("table"),
      SCREENSAVER_VIEW_SWITCH_MS
    );
    return () => clearTimeout(switchTimerId);
  }, [screensaverPhase, loading]);

  // Screensaver: the farm phase picks a random windfarm and zooms to it.
  useEffect(() => {
    if (screensaverPhase !== "farm" || loading || windfarms.length === 0) return;
    if (selectedFarm) return;
    const farm = windfarms[Math.floor(Math.random() * windfarms.length)];
    setFocusRequest({ farm, key: Date.now() });
    setSelectedFarm(farm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screensaverPhase, loading, windfarms]);

  // Screensaver: the farm phase is ready once the highlights have loaded.
  useEffect(() => {
    if (
      screensaverPhase === "farm" &&
      selectedFarm &&
      !detailLoading &&
      farmDetail
    ) {
      onScreensaverReadyRef.current?.("farm");
    }
  }, [screensaverPhase, selectedFarm, detailLoading, farmDetail]);

  return (
    <section className="bd-section bd-windfarms-section">
      <div className="bd-leads-header">
        <h2>Active Windfarms</h2>
        <select
          className="bd-access-mode"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {WINDOW_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {counts && (
        <div className="bd-access-counts bd-windfarms-counts">
          <div className="bd-chip-group">
            <span className="bd-chip-group-label">Coverage</span>
            <span className="bd-access-chip bd-access-chip-paid">
              {counts.windfarms} subscribed windfarms
            </span>
            <span className="bd-access-chip">{clientCount} clients</span>
            <span className="bd-access-chip">{counts.turbines} turbines</span>
          </div>
          <div className="bd-chip-divider" />
          <div className="bd-chip-group">
            <span className="bd-chip-group-label">
              Lightning ({
                WINDOW_OPTIONS.find((o) => o.value === days)?.label.toLowerCase()
              })
            </span>
            <span className="bd-access-chip">
              {counts.strikes} lightning strikes
            </span>
            <span className="bd-access-chip bd-access-chip-review">
              {counts.outside_iec} outside IEC
            </span>
          </div>
        </div>
      )}

      {error && (
        <p className="bd-error">
          Could not load active windfarms. Please try again.
        </p>
      )}
      {loading && <TurbineAnimation overlayClassname="bd-windfarms-loading" />}

      {!loading && !error && windfarms.length === 0 && (
        <p className="bd-empty">
          No windfarms with an active subscription found.
        </p>
      )}

      {!loading && windfarms.length > 0 && (
        <div className="bd-windfarms-layout">
          <WplWindfarmsMap
            windfarms={windfarms}
            focusRequest={focusRequest}
            onFarmClick={setSelectedFarm}
            selectedFarmId={selectedFarm?.windfarm_id}
            selectedFarmTurbines={farmDetail?.turbines}
            highlightedFarmId={hoveredFarmId}
          />

          {selectedFarm ? (
            <WplWindfarmDetail
              farm={selectedFarm}
              detail={farmDetail}
              loading={detailLoading}
              error={detailError}
              onClose={closeDetail}
            />
          ) : (
          <div className="bd-chart-card bd-windfarms-chart-card">
            <div className="bd-chart-header">
              <div className="bd-chart-header-row">
                <h3>Lightning Activity</h3>
                <div className="bd-view-toggle" role="group">
                  <button
                    type="button"
                    className={
                      activityView === "chart" ? "bd-view-toggle-active" : ""
                    }
                    onClick={() => setActivityView("chart")}
                  >
                    Chart
                  </button>
                  <button
                    type="button"
                    className={
                      activityView === "table" ? "bd-view-toggle-active" : ""
                    }
                    onClick={() => setActivityView("table")}
                  >
                    Table
                  </button>
                </div>
              </div>
              <p>
                {activityView === "chart"
                  ? "Strikes per subscribed windfarm in the selected window. " +
                    "Click a bar or a windfarm on the map to see its highlights."
                  : "Subscribed windfarms in the selected window. " +
                    "Click a row to see the windfarm's highlights."}
              </p>
            </div>

            {activityView === "table" && (
              <div className="bd-windfarms-chart-scroll">
                <table className="bd-table bd-farm-table">
                  <thead>
                    <tr>
                      {FARM_COLUMNS.map((column) => (
                        <th
                          key={column.key}
                          className="bd-th-sortable"
                          onClick={() => toggleFarmSort(column.key)}
                        >
                          {column.label}
                          {farmSort.key === column.key
                            ? farmSort.dir === "desc"
                              ? " ↓"
                              : " ↑"
                            : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFarms.map((farm) => (
                      <tr
                        key={farm.windfarm_id}
                        onClick={() => focusFarm(farm)}
                        onMouseEnter={() => setHoveredFarmId(farm.windfarm_id)}
                        onMouseLeave={() => setHoveredFarmId(null)}
                      >
                        <td>{farm.windfarm_name}</td>
                        <td>{farm.customer || farm.owner || "—"}</td>
                        <td>{farm.turbine_count}</td>
                        <td>{farm.strike_count}</td>
                        <td
                          className={
                            farm.outside_iec_count > 0 ? "bd-td-iec" : ""
                          }
                        >
                          {farm.outside_iec_count || "—"}
                        </td>
                        <td>
                          {farm.last_notification_at
                            ? formatDateTime(farm.last_notification_at)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activityView === "chart" && (
            <div className="bd-windfarms-chart-scroll">
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ left: 10, right: 20 }}
                >
                  <CartesianGrid stroke={chartTheme.grid} horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fill: chartTheme.muted, fontSize: chartFont(13) }}
                    axisLine={{ stroke: chartTheme.grid }}
                    tickLine={{ stroke: chartTheme.grid }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    interval={0}
                    tick={
                      <FarmAxisTick
                        onSelect={(name) =>
                          focusFarm(
                            chartData.find((entry) => entry.name === name)?.farm
                          )
                        }
                        onHover={hoverFarmByName}
                      />
                    }
                    axisLine={{ stroke: chartTheme.grid }}
                    tickLine={{ stroke: chartTheme.grid }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255, 255, 255, 0.08)" }}
                    formatter={(value, key) => [
                      value,
                      key === "outside_iec" ? "Outside IEC" : "Within IEC",
                    ]}
                    contentStyle={{
                      background: chartTheme.tooltipBg,
                      border: `1px solid ${chartTheme.border}`,
                      color: chartTheme.text,
                    }}
                    itemStyle={{ color: chartTheme.text }}
                    labelStyle={{ color: chartTheme.muted }}
                  />
                  <Bar
                    isAnimationActive={false}
                    dataKey="within_iec"
                    stackId="strikes"
                    fill={chartTheme.accentBrand}
                    cursor="pointer"
                    onClick={(entry) => focusFarm(entry?.farm)}
                  />
                  <Bar
                    isAnimationActive={false}
                    dataKey="outside_iec"
                    stackId="strikes"
                    fill={chartTheme.accentAlert}
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(entry) => focusFarm(entry?.farm)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            )}
          </div>
          )}
        </div>
      )}
    </section>
  );
}
